import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { Scale, ArrowLeft, TrendingDown, TrendingUp, Wallet, RefreshCw, Plus, Minus, History, AlertTriangle, GitCompare } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import BackOrHomeButton from '@/components/BackOrHomeButton';
import { useReopenModalOnMount } from '@/lib/stockModalRestore';
import { useMarketTheme } from '@/hooks/useMarketTheme';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import StockModal from '@/components/StockModal';
import CompareModal from '@/components/CompareModal';
import SiteDisclaimer from '@/components/SiteDisclaimer';
import InfoTip from '@/components/InfoTip';
import RebalancerSnapshotDiff from '@/components/RebalancerSnapshotDiff';
import { fetchStockWithFallback } from '@/lib/stockFetch';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Sector color palette (bar fill color per sector)
const SECTOR_COLOR = {
  'Technology':             'bg-violet-500',
  'Healthcare':             'bg-pink-500',
  'Financial Services':     'bg-sky-500',
  'Energy':                 'bg-orange-500',
  'Utilities':              'bg-emerald-600',
  'Real Estate':            'bg-amber-600',
  'Consumer Cyclical':      'bg-rose-500',
  'Consumer Defensive':     'bg-teal-500',
  'Industrials':            'bg-indigo-500',
  'Basic Materials':        'bg-yellow-600',
  'Communication Services': 'bg-fuchsia-500',
  'ETF / Other':            'bg-slate-500',
};

// "Jun 12, 2026 · 3:42 PM" — label for the snapshot dropdown.
function formatSnapDate(iso) {
  try {
    const d = new Date(iso);
    return `${d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })} · ${d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })}`;
  } catch {
    return iso || '';
  }
}

// Compact summary card used in the rebalancer toolbar.
// All cards share equal width/height via CSS Grid auto-cols-fr + h-full.
function MiniCard({ label, value, valueColor, subtitle, testId }) {
  return (
    <div className="rounded-lg border bg-card px-2 sm:px-4 py-1.5 sm:py-2 h-full flex flex-col items-center justify-center text-center min-w-0" data-testid={testId}>
      <p className="text-[10px] sm:text-[11px] font-semibold uppercase tracking-wider text-muted-foreground leading-none whitespace-nowrap overflow-hidden text-ellipsis">{label}</p>
      <p className={`text-sm sm:text-xl font-bold font-mono mt-0.5 sm:mt-1 leading-tight whitespace-nowrap overflow-hidden text-ellipsis ${valueColor || ''}`}>{value}</p>
      {subtitle && <p className="text-[9px] sm:text-[10px] text-muted-foreground leading-none mt-0.5 whitespace-nowrap">{subtitle}</p>}
    </div>
  );
}

// Custom slider — draggable ball on a colored bar
function SectorSlider({ sector, currentPct, targetPct, onChange, onCommit }) {
  const barRef = useRef(null);
  const [dragging, setDragging] = useState(false);
  // Keep callbacks in refs so the native listeners (attached once) always use
  // the latest values without needing to re-bind on every render.
  const onChangeRef = useRef(onChange);
  const onCommitRef = useRef(onCommit);
  useEffect(() => { onChangeRef.current = onChange; }, [onChange]);
  useEffect(() => { onCommitRef.current = onCommit; }, [onCommit]);

  const pctToX = (pct) => `${Math.max(0, Math.min(100, pct))}%`;

  // Round 96 (Feb 2026) — Mobile fix. React's synthetic onTouchStart is
  // passive-by-default since React 17, so e.preventDefault() inside it is
  // silently ignored. On iOS that means a long-press triggers the text
  // selection callout (the "popup modal text" users saw) instead of starting
  // the drag. Solution: attach a NATIVE touchstart listener with
  // { passive: false } so preventDefault actually fires. Also add
  // touch-action:none + WebkitTouchCallout:none on the bar div so the browser
  // doesn't claim the gesture for scrolling/selection.
  useEffect(() => {
    const bar = barRef.current;
    if (!bar) return;

    const startDrag = (e) => {
      if (e.cancelable) e.preventDefault();
      setDragging(true);
      const rect = bar.getBoundingClientRect();

      const moveHandler = (ev) => {
        if (ev.cancelable) ev.preventDefault();
        const clientX = ev.touches ? ev.touches[0].clientX : ev.clientX;
        const x = clientX - rect.left;
        const pct = Math.round(Math.max(0, Math.min(100, (x / rect.width) * 100)));
        onChangeRef.current?.(pct);
      };
      const upHandler = () => {
        document.removeEventListener('mousemove', moveHandler);
        document.removeEventListener('mouseup', upHandler);
        document.removeEventListener('touchmove', moveHandler);
        document.removeEventListener('touchend', upHandler);
        document.removeEventListener('touchcancel', upHandler);
        setDragging(false);
        onCommitRef.current?.();
      };
      document.addEventListener('mousemove', moveHandler);
      document.addEventListener('mouseup', upHandler);
      document.addEventListener('touchmove', moveHandler, { passive: false });
      document.addEventListener('touchend', upHandler);
      document.addEventListener('touchcancel', upHandler);
      // Move immediately on initial press so a tap snaps to position too
      moveHandler(e);
    };

    bar.addEventListener('mousedown', startDrag);
    bar.addEventListener('touchstart', startDrag, { passive: false });
    return () => {
      bar.removeEventListener('mousedown', startDrag);
      bar.removeEventListener('touchstart', startDrag);
    };
  }, []);

  const color = SECTOR_COLOR[sector] || 'bg-slate-500';

  return (
    <div className="select-none" data-testid={`rebalancer-sector-row-${sector}`}>
      <div className="flex items-center justify-between mb-0.5">
        <InfoTip
          k={`sector_${sector}`}
          className="!no-underline !decoration-transparent min-w-0 flex-1 text-xs font-semibold"
          iconSize={9}
        >
          <span className="truncate">{sector}</span>
        </InfoTip>
        <div className="flex items-center gap-1.5 text-[11px] shrink-0">
          <span className="text-muted-foreground">Cur: <span className="font-mono font-semibold text-foreground">{currentPct.toFixed(1)}%</span></span>
          <span className="text-muted-foreground">→</span>
          <input
            type="number"
            min="0"
            max="100"
            value={targetPct}
            onChange={(e) => {
              const v = Math.max(0, Math.min(100, parseInt(e.target.value || '0', 10)));
              onChange(v);
            }}
            onBlur={onCommit}
            className="w-11 px-1 py-0 text-[11px] font-mono text-right rounded border bg-card"
            data-testid={`rebalancer-target-input-${sector}`}
          />
          <span className="text-[10px] text-muted-foreground">%</span>
        </div>
      </div>
      <div
        ref={barRef}
        className="relative h-2 rounded-full bg-muted cursor-pointer"
        style={{
          touchAction: 'none',
          WebkitTouchCallout: 'none',
          WebkitUserSelect: 'none',
          userSelect: 'none',
        }}
        data-testid={`rebalancer-slider-${sector}`}
      >
        {/* Current allocation (ghost bar) */}
        <div
          className={`absolute top-0 left-0 h-full rounded-full ${color} opacity-30`}
          style={{ width: pctToX(currentPct) }}
        />
        {/* Target fill (solid) */}
        <div
          className={`absolute top-0 left-0 h-full rounded-full ${color}`}
          style={{ width: pctToX(targetPct) }}
        />
        {/* Draggable ball */}
        <div
          className="absolute top-1/2 -translate-y-1/2 w-4 h-4 rounded-full bg-white dark:bg-slate-100 border-2 border-foreground shadow hover:scale-110 active:scale-95 transition-transform pointer-events-none"
          style={{ left: `calc(${pctToX(targetPct)} - 8px)` }}
        />
        {/* Floating % tooltip — shown only while actively dragging.
            Positioned above the thumb so the user can read the exact
            value without hunting for the number input. */}
        <div
          className={`absolute -top-7 pointer-events-none transition-opacity duration-150 ${dragging ? 'opacity-100' : 'opacity-0'}`}
          style={{ left: `calc(${pctToX(targetPct)} - 18px)` }}
          aria-hidden={!dragging}
          data-testid={`rebalancer-slider-tooltip-${sector}`}
        >
          <div className={`relative ${color} text-white text-[10px] font-mono font-bold px-1.5 py-0.5 rounded shadow-md min-w-[36px] text-center`}>
            {targetPct}%
            {/* Downward arrow — same sector color, drawn via clip-path so
                we don't have to maintain a parallel border-color map. */}
            <div
              className={`absolute left-1/2 -translate-x-1/2 top-full -mt-px w-2 h-1.5 ${color}`}
              style={{ clipPath: 'polygon(0 0, 100% 0, 50% 100%)' }}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

export default function Rebalancer() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { isDark } = useMarketTheme();

  // ------- Session-persisted state so the page survives navigation back -----
  const loadSS = (key, fallback) => {
    try {
      const v = sessionStorage.getItem(key);
      if (v !== null) return JSON.parse(v);
    } catch {}
    return fallback;
  };
  const saveSS = (key, val) => {
    try { sessionStorage.setItem(key, JSON.stringify(val)); } catch {}
  };

  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [targets, setTargets] = useState(() => loadSS('rebalancer:targets', {}));
  const [fundMode, setFundMode] = useState(() => loadSS('rebalancer:fundMode', 'sell'));
  const [cashAvailable, setCashAvailable] = useState(() => loadSS('rebalancer:cash', ''));
  const [cashConfirm, setCashConfirm] = useState(null); // { kind: 'add'|'withdraw', amount: number }
  const [cashBusy, setCashBusy] = useState(false);
  const [snapModal, setSnapModal] = useState(null); // {sector, diff} or null

  // Execution plan snapshot: what the user committed to when they navigated
  // out to a sector page to buy stocks. Persists forever until user clicks Reset.
  // Shape: { baselineCash, baselineSectors: { sector: value }, plannedBuys: { sector: $ }, plannedSells: { sector: $ } }
  const [plan, setPlan] = useState(() => loadSS('rebalancer:plan', null));
  const [warningModal, setWarningModal] = useState(false);
  const warningDismissedRef = useRef(false);

  // Stock modal state (opens when a ticker pill is clicked)
  const [selectedStock, setSelectedStock] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  // Round 95bu (Jun 2026) — inline Compare for the Rebalancer's Stock
  // Modal (opened from any sell/buy pill). Previously had no Compare
  // handler at all so the link was hidden.
  const [compareOpen, setCompareOpen] = useState(false);
  const [compareTicker, setCompareTicker] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [prefetchedNews, setPrefetchedNews] = useState(null);
  const [watchlist, setWatchlist] = useState([]);
  const [modalInitialTab, setModalInitialTab] = useState(undefined);

  // Persist on change
  useEffect(() => {
    if (Object.keys(targets).length > 0) saveSS('rebalancer:targets', targets);
  }, [targets]);
  useEffect(() => { saveSS('rebalancer:fundMode', fundMode); }, [fundMode]);
  useEffect(() => { saveSS('rebalancer:cash', cashAvailable); }, [cashAvailable]);
  useEffect(() => { saveSS('rebalancer:plan', plan); }, [plan]);

  const lastEditedSectorRef = useRef(null);
  // Ref so handleCommit always sees the latest targets, not a stale closure
  // captured when the drag started.
  const targetsRef = useRef({});
  useEffect(() => { targetsRef.current = targets; }, [targets]);

  // Load portfolio breakdown + refetch whenever portfolio changes
  const loadData = useCallback(() => {
    if (!user) { setLoading(false); return; }
    setLoading(true);
    axios.get(`${API}/portfolio/rebalancer`, { withCredentials: true })
      .then((r) => {
        setData(r.data);
        // Initialize targets from current allocation ONLY if session didn't have any
        setTargets((prev) => {
          if (Object.keys(prev).length > 0) return prev;
          const t = {};
          (r.data.sectors || []).forEach((s) => { t[s.sector] = Math.round(s.current_pct); });
          return t;
        });
      })
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [user]);

  useEffect(() => { loadData(); }, [loadData]);

  // ── Round 95q — Safety-net snapshots ─────────────────────────────
  // On page entry we auto-save a full snapshot (positions + cash) of
  // the active portfolio. The backend dedupes identical holdings and
  // keeps max 3. The dropdown below lets the user restore any of them
  // if they wreck their portfolio while sandboxing buys/sells.
  const [snapshots, setSnapshots] = useState([]);
  const [selectedSnapId, setSelectedSnapId] = useState('');
  const [restoreTarget, setRestoreTarget] = useState(null); // snapshot obj or null
  const [compareTarget, setCompareTarget] = useState(null); // snapshot obj for diff modal
  const [restoring, setRestoring] = useState(false);
  const snapshotTakenRef = useRef(false);

  const loadSnapshots = useCallback(() => {
    if (!user) return;
    axios.get(`${API}/portfolio/rebalancer/snapshots`, { withCredentials: true })
      .then((r) => {
        const list = r.data?.snapshots || [];
        setSnapshots(list);
        setSelectedSnapId((prev) => (list.some((s) => s.snapshot_id === prev) ? prev : (list[0]?.snapshot_id || '')));
      })
      .catch(() => {});
  }, [user]);

  useEffect(() => {
    if (!user || snapshotTakenRef.current) return;
    snapshotTakenRef.current = true;
    axios.post(`${API}/portfolio/rebalancer/snapshots`, {}, { withCredentials: true })
      .then(() => loadSnapshots())
      .catch(() => loadSnapshots());
  }, [user, loadSnapshots]);

  const submitRestore = async () => {
    if (!restoreTarget || restoring) return;
    setRestoring(true);
    try {
      const { data: res } = await axios.post(
        `${API}/portfolio/rebalancer/snapshots/${restoreTarget.snapshot_id}/restore`,
        {},
        { withCredentials: true },
      );
      toast.success('Portfolio restored', {
        description: `${res.position_count} positions and $${Number(res.cash_balance || 0).toLocaleString()} cash from ${formatSnapDate(res.created_at)}.`,
      });
      // Stale targets/plan belong to the wrecked portfolio — clear them.
      clearPlan();
      try { sessionStorage.removeItem('rebalancer:targets'); } catch {}
      setTargets({});
      window.dispatchEvent(new Event('portfolio-changed'));
      window.dispatchEvent(new Event('cash-changed'));
      loadData();
      loadSnapshots();
    } catch (err) {
      toast.error('Restore failed', { description: err?.response?.data?.detail || err?.message });
    }
    setRestoreTarget(null);
    setRestoring(false);
  };

  // Also fetch watchlist for the stock modal's star toggle
  useEffect(() => {
    if (!user) return;
    axios.get(`${API}/portfolio`, { withCredentials: true })
      .then((r) => setWatchlist((r.data || []).map((x) => x.ticker)))
      .catch(() => {});
  }, [user]);

  // Refresh data when portfolio or cash changes (e.g. user sells or deposits)
  useEffect(() => {
    const handler = () => loadData();
    window.addEventListener('portfolio-changed', handler);
    window.addEventListener('cash-changed', handler);
    return () => {
      window.removeEventListener('portfolio-changed', handler);
      window.removeEventListener('cash-changed', handler);
    };
  }, [loadData]);

  // Cash deposit/withdraw confirm → API call → refresh cash card
  const submitCashChange = async () => {
    if (!cashConfirm || cashBusy) return;
    setCashBusy(true);
    const endpoint = cashConfirm.kind === 'add' ? 'deposit' : 'withdraw';
    try {
      await axios.post(
        `${API}/portfolio/cash/${endpoint}`,
        { amount: Number(cashConfirm.amount) },
        { withCredentials: true }
      );
      // Fire the global event so cash card + rebalancer both refetch
      window.dispatchEvent(new Event('cash-changed'));
      setCashAvailable('');
    } catch (err) {
      // Backend clamps at 0 on withdraw, but swallow any error silently per user spec
      console.error('Cash change failed:', err);
    }
    setCashConfirm(null);
    setCashBusy(false);
  };

  // Open stock modal for a ticker
  const handleTickerClick = async (ticker, initialTab = undefined) => {
    // Save the plan snapshot when user opens a stock to manage
    // (they may add/sell/remove positions, so we need to track progress).
    savePlanSnapshot();
    setModalInitialTab(initialTab);
    setLoadingDetail(true);
    setModalOpen(true);
    setPrefetchedNews(null);
    axios.get(`${API}/stocks/${ticker}/news`, { withCredentials: true })
      .then((r) => setPrefetchedNews(r.data))
      .catch(() => {});
    try {
      const sd = await fetchStockWithFallback(ticker, {
        timeoutMs: 3500,
        onFresh: (fresh) => setSelectedStock(fresh),
      });
      setSelectedStock(sd);
    } catch { setSelectedStock(null); }
    setLoadingDetail(false);
  };

  // Round 79 — StockModal back-restore. Rebalancer's stock-open
  // function is `handleTickerClick` (it also auto-saves a plan
  // snapshot first). Re-opening on Back calls the same function so
  // the snapshot path stays consistent.
  useReopenModalOnMount(handleTickerClick);

  const toggleWatchlist = async (ticker) => {
    if (!user) return;
    if (watchlist.includes(ticker)) {
      await axios.delete(`${API}/portfolio/${ticker}`, { withCredentials: true });
      setWatchlist((w) => w.filter((t) => t !== ticker));
    } else {
      await axios.post(`${API}/portfolio`, { ticker }, { withCredentials: true });
      setWatchlist((w) => [...w, ticker]);
    }
    window.dispatchEvent(new Event('watchlist-changed'));
  };

  const totalTarget = useMemo(
    () => Object.values(targets).reduce((a, b) => a + (b || 0), 0),
    [targets],
  );

  const portfolioValue = data?.portfolio_value || 0;
  const cashBalance = Number(data?.cash_balance) || 0;
  // User-entered extra cash (on top of their actual cash balance)
  const extraCashNum = Number(cashAvailable) || 0;

  // Pool of money to redistribute depends on the fund mode.
  // - 'sell': only existing positions are redistributed
  // - 'cash': positions stay put; only extra cash + existing cash drives BUYs
  // - 'both': sell positions + include existing cash + extra cash
  const rebalancePool = fundMode === 'cash'
    ? portfolioValue + cashBalance + extraCashNum
    : fundMode === 'sell'
      ? portfolioValue
      : portfolioValue + cashBalance + extraCashNum;

  // Compute deltas per sector
  const rows = useMemo(() => {
    if (!data) return [];
    return data.sectors.map((s) => {
      const tgtPct = targets[s.sector] ?? 0;
      const targetValue = (tgtPct / 100) * rebalancePool;
      const delta = targetValue - s.current_value;
      return {
        ...s,
        target_pct: tgtPct,
        target_value: targetValue,
        delta, // positive = BUY, negative = SELL
      };
    });
  }, [data, targets, rebalancePool]);

  const handleTargetChange = (sector, pct) => {
    lastEditedSectorRef.current = sector;
    setTargets((prev) => ({ ...prev, [sector]: pct }));
  };

  // Round 95q — sector concentration risk (shown under Rebalancing
  // Recommendations). Any bucket holding ≥40% of stock value is
  // flagged; the ETF / Other bucket gets softer wording since one
  // broad-market fund can be internally diversified.
  const concentratedSectors = useMemo(() => {
    if (!data || !portfolioValue) return [];
    return (data.sectors || [])
      .filter((s) => s.current_value > 0 && s.current_pct >= 40)
      .map((s) => ({ sector: s.sector, pct: s.current_pct, isEtfBucket: s.sector === 'ETF / Other' }))
      .sort((a, b) => b.pct - a.pct);
  }, [data, portfolioValue]);

  // On drag commit, check if total is 97-99% or 101-103% -> show snap modal
  const handleCommit = () => {
    const sum = Object.values(targetsRef.current).reduce((a, b) => a + (b || 0), 0);
    if ((sum >= 97 && sum <= 99) || (sum >= 101 && sum <= 103)) {
      const sector = lastEditedSectorRef.current;
      if (!sector) return;
      const diff = 100 - sum;
      setSnapModal({ sector, diff, direction: sum < 100 ? 'low' : 'high', sum });
    }
  };

  const applySnap = () => {
    if (!snapModal) return;
    setTargets((prev) => ({
      ...prev,
      [snapModal.sector]: Math.max(0, Math.min(100, (prev[snapModal.sector] || 0) + snapModal.diff)),
    }));
    setSnapModal(null);
  };

  const resetTargets = () => {
    if (!data) return;
    const t = {};
    data.sectors.forEach((s) => { t[s.sector] = Math.round(s.current_pct); });
    setTargets(t);
    try { sessionStorage.removeItem('rebalancer:targets'); } catch {}
    clearPlan();
  };

  // Build BUY/SELL recommendation list
  const recommendations = useMemo(() => {
    const recs = [];
    rows.forEach((r) => {
      if (Math.abs(r.delta) < 1) return; // ignore sub-$1 drift
      if (r.delta > 0) {
        // BUY: include current holdings so we can split the buy $ across them
        recs.push({
          action: 'BUY',
          sector: r.sector,
          amount: r.delta,
          target_value: r.target_value,
          tickers: r.tickers || [],
        });
      } else {
        // Any sector where target < current needs to be sold, regardless of fund mode.
        // (The fund mode only changes which cash is available to fund the BUY side.)
        const amt = -r.delta;
        recs.push({
          action: 'SELL',
          sector: r.sector,
          amount: amt,
          target_value: r.target_value,
          target_pct: r.target_pct,
          tickers: r.tickers || [],
        });
      }
    });
    // Sort: SELL first then BUY; within each, largest $ first
    recs.sort((a, b) => {
      if (a.action !== b.action) return a.action === 'SELL' ? -1 : 1;
      return b.amount - a.amount;
    });
    return recs;
  }, [rows, fundMode]);

  // Apply the saved plan (if any) to the raw recommendations.
  // For BUY recs: remaining = max(0, plannedBuy - actuallyBought)
  // For SELL recs: remaining = max(0, plannedSell - actuallySold)
  // Sectors where remaining is near zero are marked { done: true } so the UI shows ✓ Done.
  const adjustedRecs = useMemo(() => {
    if (!plan || !data) return recommendations;

    const baselineMap = plan.baselineSectors || {};
    const sectorCurrent = {};
    (data.sectors || []).forEach((s) => { sectorCurrent[s.sector] = s.current_value; });

    return recommendations.map((rec) => {
      const baseline = baselineMap[rec.sector] ?? 0;
      const current = sectorCurrent[rec.sector] ?? 0;
      if (rec.action === 'BUY') {
        const planned = plan.plannedBuys?.[rec.sector] ?? 0;
        const bought = Math.max(0, current - baseline);
        const remaining = Math.max(0, planned - bought);
        // If we had no plan for this sector, show the live rec as-is
        if (planned <= 0) return rec;
        if (remaining < 1) return { ...rec, amount: 0, done: true };
        return { ...rec, amount: remaining };
      } else {
        const planned = plan.plannedSells?.[rec.sector] ?? 0;
        const sold = Math.max(0, baseline - current);
        const remaining = Math.max(0, planned - sold);
        if (planned <= 0) return rec;
        if (remaining < 1) return { ...rec, amount: 0, done: true };
        return { ...rec, amount: remaining };
      }
    });
  }, [recommendations, plan, data]);

  // Plan progress summary for the banner
  const planProgress = useMemo(() => {
    if (!plan || !data) return null;
    const baselineMap = plan.baselineSectors || {};
    const sectorCurrent = {};
    (data.sectors || []).forEach((s) => { sectorCurrent[s.sector] = s.current_value; });

    const plannedSectors = new Set([
      ...Object.keys(plan.plannedBuys || {}).filter((k) => plan.plannedBuys[k] > 0),
      ...Object.keys(plan.plannedSells || {}).filter((k) => plan.plannedSells[k] > 0),
    ]);
    if (plannedSectors.size === 0) return null;

    let doneCount = 0;
    let plannedBuyTotal = 0;
    let plannedSellTotal = 0;
    let boughtTotal = 0;
    let soldTotal = 0;

    plannedSectors.forEach((sector) => {
      const baseline = baselineMap[sector] ?? 0;
      const current = sectorCurrent[sector] ?? 0;
      const plannedBuy = plan.plannedBuys?.[sector] ?? 0;
      const plannedSell = plan.plannedSells?.[sector] ?? 0;

      if (plannedBuy > 0) {
        const bought = Math.max(0, current - baseline);
        plannedBuyTotal += plannedBuy;
        boughtTotal += Math.min(bought, plannedBuy);
        if (bought + 1 >= plannedBuy) doneCount++;
      }
      if (plannedSell > 0) {
        const sold = Math.max(0, baseline - current);
        plannedSellTotal += plannedSell;
        soldTotal += Math.min(sold, plannedSell);
        if (sold + 1 >= plannedSell) doneCount++;
      }
    });

    const totalTargets = Object.values(plan.plannedBuys || {}).filter((v) => v > 0).length
      + Object.values(plan.plannedSells || {}).filter((v) => v > 0).length;
    const spent = boughtTotal + soldTotal;
    const planned = plannedBuyTotal + plannedSellTotal;
    const pct = planned > 0 ? Math.round((spent / planned) * 100) : 0;
    const allDone = totalTargets > 0 && doneCount >= totalTargets;

    return {
      doneCount,
      totalTargets,
      spent,
      planned,
      pct,
      allDone,
      plannedBuyTotal,
      plannedSellTotal,
      boughtTotal,
      soldTotal,
    };
  }, [plan, data]);

  // Warning: user has executed more BUYs than their baseline cash + executed SELLs could cover.
  // This catches the "bought without selling first" case.
  useEffect(() => {
    if (!plan || !data || warningDismissedRef.current) return;
    const baselineCash = Number(plan.baselineCash) || 0;
    const baselineMap = plan.baselineSectors || {};
    let executedBuys = 0;
    let executedSells = 0;
    (data.sectors || []).forEach((s) => {
      const base = baselineMap[s.sector] ?? 0;
      const diff = s.current_value - base;
      if (diff > 0 && (plan.plannedBuys?.[s.sector] ?? 0) > 0) executedBuys += diff;
      if (diff < 0 && (plan.plannedSells?.[s.sector] ?? 0) > 0) executedSells += -diff;
    });
    // If total executed buys exceed baseline cash + executed sells, they spent money they didn't have here.
    if (executedBuys > baselineCash + executedSells + 1) {
      setWarningModal(true);
    }
  }, [plan, data]);

  // Build a plan snapshot from current recommendations + baseline data.
  // Called right before we navigate to a sector page (auto-save).
  const savePlanSnapshot = () => {
    if (!data) return;
    const baselineSectors = {};
    (data.sectors || []).forEach((s) => { baselineSectors[s.sector] = s.current_value; });
    const plannedBuys = {};
    const plannedSells = {};
    recommendations.forEach((r) => {
      if (r.action === 'BUY') plannedBuys[r.sector] = Number(r.amount) || 0;
      else plannedSells[r.sector] = Number(r.amount) || 0;
    });
    warningDismissedRef.current = false;
    setPlan({
      createdAt: Date.now(),
      baselineCash: Number(data.cash_balance) || 0,
      baselineTotalValue: Number(data.portfolio_value) || 0,
      baselineSectors,
      plannedBuys,
      plannedSells,
    });
  };

  const clearPlan = () => {
    warningDismissedRef.current = true;
    setPlan(null);
    try { sessionStorage.removeItem('rebalancer:plan'); } catch {}
    setWarningModal(false);
  };

  // Split a whole-dollar amount across N tickers; remainder goes to the last.
  // e.g. splitAmount(34, 3) => [11, 11, 12]
  const splitAmount = (total, n) => {
    if (n <= 0) return [];
    const whole = Math.round(total);
    const per = Math.floor(whole / n);
    const remainder = whole - per * n;
    return Array.from({ length: n }, (_, i) => per + (i === n - 1 ? remainder : 0));
  };

  const statusColor =
    totalTarget === 100 ? 'text-emerald-500'
      : totalTarget > 100 ? 'text-red-500'
      : 'text-amber-500';

  if (!user) {
    return (
      <div className="min-h-screen min-h-[100svh] flex items-center justify-center bg-background px-4">
        <div className="text-center">
          <Scale className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
          <h2 className="text-xl font-bold mb-2">Portfolio Rebalancer</h2>
          <p className="text-sm text-muted-foreground mb-4">Sign in to use the rebalancer.</p>
          <Link to="/" className="text-sm text-primary hover:underline">Back to home</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen min-h-[100svh] bg-background" data-testid="rebalancer-page">
      <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-3 pb-0 space-y-3">
        {/* Title row */}
        <div className="flex items-center justify-center gap-2 flex-wrap">
          <BackOrHomeButton testid="rebalancer-back-or-home-btn" showEscHint={false} />
          <Scale className="w-5 h-5 text-primary" />
          <h1 className="text-base sm:text-lg font-bold" data-testid="rebalancer-title">Portfolio Rebalancer</h1>
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-48">
            <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
          </div>
        ) : !data || data.position_count === 0 ? (
          <div className="rounded-xl border bg-card p-8 text-center" data-testid="rebalancer-empty">
            <Scale className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
            <p className="text-lg font-medium">No positions yet</p>
            <p className="text-sm text-muted-foreground mt-1">Add some positions in "My Portfolio" first to use the rebalancer.</p>
            <button
              onClick={() => navigate('/?view=portfolio')}
              className="mt-3 inline-flex items-center gap-1.5 text-sm font-semibold text-primary hover:underline"
            >
              <ArrowLeft className="w-4 h-4" /> My Portfolio
            </button>
          </div>
        ) : (
          <>
            {/* Desktop toolbar (single line): Back • Summary mini-cards • Reset */}
            <div className="hidden sm:flex items-stretch gap-2" data-testid="rebalancer-summary">
              <button
                onClick={() => navigate('/?view=portfolio')}
                className="inline-flex items-center gap-1.5 px-3 rounded-lg border bg-card text-xs font-semibold text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                data-testid="rebalancer-back-btn"
              >
                <ArrowLeft className="w-3.5 h-3.5" />
                My Portfolio
              </button>

              <div className="flex-1 grid grid-flow-col auto-cols-fr gap-2 min-w-0">
                <MiniCard label="Stock Value" value={`$${portfolioValue.toLocaleString(undefined, { maximumFractionDigits: 0 })}`} />
                <MiniCard label="Cash" value={`$${cashBalance.toLocaleString(undefined, { maximumFractionDigits: 0 })}`} valueColor="text-emerald-700 dark:text-emerald-400" testId="rebalancer-cash-card" />
                <MiniCard label="Positions" value={data.position_count} />
                <MiniCard
                  label="Target"
                  value={`${totalTarget}%`}
                  valueColor={statusColor}
                  subtitle={totalTarget === 100 ? 'balanced' : totalTarget > 100 ? `over ${totalTarget - 100}%` : `under ${100 - totalTarget}%`}
                  testId="rebalancer-target-total"
                />
                <MiniCard
                  label="Sectors"
                  value={data.sectors.filter((s) => s.current_value > 0).length}
                  subtitle="held"
                />
              </div>

              <button
                onClick={resetTargets}
                className="inline-flex items-center gap-1.5 px-3 rounded-lg border bg-card text-xs font-medium text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                data-testid="rebalancer-reset-btn"
              >
                <RefreshCw className="w-3.5 h-3.5" /> Reset
              </button>
            </div>

            {/* Mobile toolbar (two rows): My Portfolio | Stock Value | Cash -- then Positions | Target | Sectors | Reset */}
            <div className="sm:hidden flex flex-col gap-2" data-testid="rebalancer-summary-mobile">
              <div className="flex items-stretch gap-2">
                <button
                  onClick={() => navigate('/?view=portfolio')}
                  className="inline-flex items-center gap-1 px-2 py-2 rounded-lg border bg-card text-[11px] font-semibold text-muted-foreground hover:text-foreground hover:bg-muted transition-colors shrink-0"
                  data-testid="rebalancer-back-btn-mobile"
                >
                  <ArrowLeft className="w-3.5 h-3.5" />
                  My Portfolio
                </button>
                <div className="flex-1 grid grid-cols-2 gap-2 min-w-0">
                  <MiniCard label="Stock Value" value={`$${portfolioValue.toLocaleString(undefined, { maximumFractionDigits: 0 })}`} />
                  <MiniCard label="Cash" value={`$${cashBalance.toLocaleString(undefined, { maximumFractionDigits: 0 })}`} valueColor="text-emerald-700 dark:text-emerald-400" testId="rebalancer-cash-card-mobile" />
                </div>
              </div>
              <div className="flex items-stretch gap-2">
                <div className="flex-1 grid grid-cols-3 gap-2 min-w-0">
                  <MiniCard label="Positions" value={data.position_count} />
                  <MiniCard
                    label="Target"
                    value={`${totalTarget}%`}
                    valueColor={statusColor}
                    subtitle={totalTarget === 100 ? 'balanced' : totalTarget > 100 ? `over ${totalTarget - 100}%` : `under ${100 - totalTarget}%`}
                    testId="rebalancer-target-total-mobile"
                  />
                  <MiniCard
                    label="Sectors"
                    value={data.sectors.filter((s) => s.current_value > 0).length}
                    subtitle="held"
                  />
                </div>
                <button
                  onClick={resetTargets}
                  className="inline-flex items-center gap-1 px-2 py-2 rounded-lg border bg-card text-[11px] font-medium text-muted-foreground hover:text-foreground hover:bg-muted transition-colors shrink-0"
                  data-testid="rebalancer-reset-btn-mobile"
                >
                  <RefreshCw className="w-3.5 h-3.5" /> Reset
                </button>
              </div>
            </div>

            {/* Plan progress banner (only shown when a plan is active) */}
            {planProgress && (
              <div
                className={`rounded-xl border-2 p-3 ${
                  planProgress.allDone
                    ? 'bg-emerald-50 dark:bg-emerald-950/30 border-emerald-500'
                    : 'bg-sky-50 dark:bg-sky-950/30 border-sky-400 dark:border-sky-600'
                }`}
                data-testid="rebalancer-plan-progress"
              >
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <div className="flex items-center gap-2 min-w-0">
                    <span className={`shrink-0 inline-flex items-center justify-center w-6 h-6 rounded-full text-white text-sm font-bold ${
                      planProgress.allDone ? 'bg-emerald-600' : 'bg-sky-500'
                    }`}>
                      {planProgress.allDone ? '✓' : '⟳'}
                    </span>
                    <div className="min-w-0">
                      <p className={`text-sm font-bold ${
                        planProgress.allDone
                          ? 'text-emerald-700 dark:text-emerald-300'
                          : 'text-sky-700 dark:text-sky-300'
                      }`}>
                        {planProgress.allDone
                          ? 'Plan complete — all targets reached!'
                          : `Plan in progress — ${planProgress.doneCount} of ${planProgress.totalTargets} sector target${planProgress.totalTargets === 1 ? '' : 's'} reached`}
                      </p>
                      <p className="text-xs text-muted-foreground mt-0.5 font-mono">
                        ${Math.round(planProgress.spent).toLocaleString()} of ${Math.round(planProgress.planned).toLocaleString()} executed
                        {planProgress.plannedBuyTotal > 0 && (
                          <> · <span className="text-emerald-700 dark:text-emerald-400">Bought ${Math.round(planProgress.boughtTotal).toLocaleString()}/${Math.round(planProgress.plannedBuyTotal).toLocaleString()}</span></>
                        )}
                        {planProgress.plannedSellTotal > 0 && (
                          <> · <span className="text-red-600 dark:text-red-400">Sold ${Math.round(planProgress.soldTotal).toLocaleString()}/${Math.round(planProgress.plannedSellTotal).toLocaleString()}</span></>
                        )}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={clearPlan}
                    className="shrink-0 inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-medium bg-white dark:bg-neutral-900 border hover:bg-muted transition-colors"
                    data-testid="rebalancer-plan-clear-btn"
                    title="Clear this plan (does NOT touch your cash or positions)"
                  >
                    Clear Plan
                  </button>
                </div>
                {/* Progress bar */}
                <div className="mt-2 h-1.5 rounded-full bg-muted overflow-hidden" aria-hidden>
                  <div
                    className={`h-full transition-all ${planProgress.allDone ? 'bg-emerald-500' : 'bg-sky-500'}`}
                    style={{ width: `${Math.min(100, planProgress.pct)}%` }}
                  />
                </div>
              </div>
            )}

            {/* Fund mode toggle */}
            <div className="rounded-xl border bg-card p-3" data-testid="rebalancer-fund-mode">
              <div className="flex items-center flex-wrap gap-2">
                <Wallet className="w-4 h-4 text-muted-foreground" />
                <span className="text-xs font-semibold mr-2">Fund rebalance with:</span>
                {['sell', 'cash', 'both'].map((m) => (
                  <button
                    key={m}
                    onClick={() => setFundMode(m)}
                    className={`px-3 py-1 rounded-full text-xs font-semibold border transition-colors ${
                      fundMode === m
                        ? 'bg-primary text-primary-foreground border-primary'
                        : 'bg-card hover:bg-muted border-border'
                    }`}
                    data-testid={`rebalancer-fund-${m}`}
                  >
                    {m === 'sell' ? 'Sell shares' : m === 'cash' ? 'Cash only' : 'Both'}
                  </button>
                ))}
                {(fundMode === 'cash' || fundMode === 'both') && (
                  <div className="flex items-center flex-wrap gap-2 ml-auto">
                    <button
                      onClick={() => {
                        const n = Number(cashAvailable);
                        if (!n || n <= 0) return;
                        setCashConfirm({ kind: 'add', amount: n });
                      }}
                      className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md text-xs font-bold border-2 border-emerald-500 bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300 hover:bg-emerald-100 dark:hover:bg-emerald-950/60 transition-colors"
                      data-testid="rebalancer-add-cash-btn"
                      title="Add this amount to your cash balance"
                    >
                      <Plus className="w-3.5 h-3.5" /> Add Cash
                    </button>
                    <input
                      type="number"
                      min="0"
                      value={cashAvailable}
                      onChange={(e) => setCashAvailable(e.target.value)}
                      placeholder="0"
                      className="w-24 px-2 py-1 text-xs font-mono rounded border bg-background"
                      data-testid="rebalancer-cash-input"
                    />
                    <button
                      onClick={() => {
                        const n = Number(cashAvailable);
                        if (!n || n <= 0) return;
                        setCashConfirm({ kind: 'withdraw', amount: n });
                      }}
                      className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md text-xs font-bold border-2 border-red-500 bg-red-50 text-red-700 dark:bg-red-950/40 dark:text-red-300 hover:bg-red-100 dark:hover:bg-red-950/60 transition-colors"
                      data-testid="rebalancer-withdraw-cash-btn"
                      title="Withdraw this amount from your cash balance"
                    >
                      <Minus className="w-3.5 h-3.5" /> Withdraw Cash
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* Round 95q — Safety-net snapshots: restore the portfolio
                to how it looked when you entered this page (up to 3
                auto-saved entry snapshots). */}
            {snapshots.length > 0 && (
              <div className="rounded-xl border bg-card p-3" data-testid="rebalancer-snapshots">
                <div className="flex items-center flex-wrap gap-2">
                  <History className="w-4 h-4 text-muted-foreground" />
                  <div className="mr-1">
                    <span className="text-xs font-semibold block">Safety net</span>
                    <span className="text-[10px] text-muted-foreground block">Auto-saved when you open this page — restore if you mess up</span>
                  </div>
                  <select
                    value={selectedSnapId}
                    onChange={(e) => setSelectedSnapId(e.target.value)}
                    className="flex-1 min-w-[220px] sm:flex-none sm:w-auto px-2 py-1.5 text-xs font-mono rounded-md border bg-background"
                    data-testid="rebalancer-snapshot-select"
                  >
                    {snapshots.map((s) => (
                      <option key={s.snapshot_id} value={s.snapshot_id}>
                        {formatSnapDate(s.created_at)} — {s.position_count} positions · ${Number(s.total_shares_value || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })} + ${Number(s.cash_balance || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })} cash
                      </option>
                    ))}
                  </select>
                  <button
                    onClick={() => {
                      const snap = snapshots.find((s) => s.snapshot_id === selectedSnapId);
                      if (snap) setCompareTarget(snap);
                    }}
                    disabled={!selectedSnapId}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-bold border-2 border-sky-500 bg-sky-50 text-sky-700 dark:bg-sky-950/40 dark:text-sky-300 hover:bg-sky-100 dark:hover:bg-sky-950/60 transition-colors disabled:opacity-50"
                    data-testid="rebalancer-snapshot-compare-btn"
                    title="See what changed since this snapshot was saved"
                  >
                    <GitCompare className="w-3.5 h-3.5" /> Compare
                  </button>
                  <button
                    onClick={() => {
                      const snap = snapshots.find((s) => s.snapshot_id === selectedSnapId);
                      if (snap) setRestoreTarget(snap);
                    }}
                    disabled={!selectedSnapId}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-bold border-2 border-violet-500 bg-violet-50 text-violet-700 dark:bg-violet-950/40 dark:text-violet-300 hover:bg-violet-100 dark:hover:bg-violet-950/60 transition-colors disabled:opacity-50"
                    data-testid="rebalancer-snapshot-restore-btn"
                    title="Restore positions + cash to this snapshot"
                  >
                    <RefreshCw className="w-3.5 h-3.5" /> Restore Portfolio
                  </button>
                </div>
              </div>
            )}

            {/* Main 2-col layout */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {/* LEFT: Sector sliders */}
              <div className="rounded-xl border bg-card p-3 space-y-2" data-testid="rebalancer-sliders">
                <div>
                  <h2 className="text-sm font-bold">Sector Allocation vs Target</h2>
                  <p className="text-[10px] text-muted-foreground">Drag the ball to set your target %</p>
                </div>
                <div className="space-y-2">
                  {rows.map((r) => (
                    <SectorSlider
                      key={r.sector}
                      sector={r.sector}
                      currentPct={r.current_pct}
                      targetPct={r.target_pct}
                      onChange={(pct) => handleTargetChange(r.sector, pct)}
                      onCommit={handleCommit}
                    />
                  ))}
                </div>
              </div>

              {/* RIGHT: Recommendations */}
              <div className="rounded-xl border bg-card p-4" data-testid="rebalancer-recommendations">
                <div className="mb-3">
                  <h2 className="text-sm font-bold">Rebalancing Recommendations</h2>
                  <p className="text-xs text-muted-foreground">
                    {fundMode === 'cash' ? 'Cash needed to reach targets' : fundMode === 'sell' ? 'Sell overweight, buy underweight' : 'Combined plan'}
                  </p>
                </div>

                {/* Round 95q — sector concentration risk banner */}
                {concentratedSectors.length > 0 && (
                  <div
                    className="mb-3 rounded-lg border-2 border-amber-400/60 bg-amber-50 dark:bg-amber-950/30 px-3 py-2"
                    data-testid="rebalancer-concentration-banner"
                  >
                    <div className="flex items-start gap-2">
                      <AlertTriangle className="w-4 h-4 text-amber-600 dark:text-amber-400 shrink-0 mt-0.5" />
                      <div className="min-w-0">
                        <p className="text-xs font-bold text-amber-800 dark:text-amber-300">
                          Concentration risk
                        </p>
                        {concentratedSectors.map((c) => (
                          <p key={c.sector} className="text-[11px] text-amber-800/90 dark:text-amber-200/80 mt-0.5">
                            <strong>{c.sector}</strong> holds <strong>{c.pct}%</strong> of this portfolio&apos;s stock value.{' '}
                            {c.isEtfBucket
                              ? 'One broad-market fund can be fine — but check for overlapping funds doing the same job.'
                              : 'A downturn in one sector hits hard — consider a target below 40% and shifting the difference to underweight sectors.'}
                          </p>
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                {adjustedRecs.length === 0 ? (
                  <div className="py-8 text-center text-sm text-muted-foreground" data-testid="rebalancer-no-recs">
                    Your targets match your current allocation. No rebalancing needed.
                  </div>
                ) : (
                  <div className="space-y-2">
                    {adjustedRecs.map((rec, i) => {
                      const isBuy = rec.action === 'BUY';
                      const isDone = !!rec.done;
                      const hasHoldings = rec.tickers && rec.tickers.length > 0;
                      // When selling and sector target is 0% (full exit), force Sell All
                      // on every ticker with that ticker's full current value — not an equal split.
                      const exitSector = !isBuy && (rec.target_pct === 0 || rec.target_value <= 0.01);
                      const splits = (!isDone && hasHoldings) ? splitAmount(rec.amount, rec.tickers.length) : [];
                      // Outer wrapper: <button> for BUY (clickable row), <div> for SELL or Done.
                      const Wrapper = (isBuy && !isDone) ? 'button' : 'div';
                      const wrapperProps = (isBuy && !isDone)
                        ? {
                            onClick: () => {
                              savePlanSnapshot();
                              navigate(`/sector/${encodeURIComponent(rec.sector)}?target=${Math.round(rec.amount)}`);
                            },
                          }
                        : {};
                      return (
                        <Wrapper
                          key={i}
                          {...wrapperProps}
                          className={`w-full text-left rounded-lg border px-3 py-2 transition-colors ${
                            isDone
                              ? 'bg-muted/40 border-border/60 cursor-default opacity-75'
                              : isBuy
                                ? 'bg-emerald-50 dark:bg-emerald-950/30 hover:bg-emerald-100 dark:hover:bg-emerald-950/50 border-emerald-200 dark:border-emerald-900 cursor-pointer'
                                : 'bg-red-50 dark:bg-red-950/30 border-red-200 dark:border-red-900 cursor-default'
                          }`}
                          data-testid={`rebalancer-rec-${rec.action.toLowerCase()}-${rec.sector}${isDone ? '-done' : ''}`}
                        >
                          <div className="flex items-center justify-between gap-2">
                            <div className="flex items-center gap-2 min-w-0">
                              <span className={`shrink-0 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold ${
                                isDone
                                  ? 'bg-muted-foreground/20 text-muted-foreground'
                                  : isBuy
                                    ? 'bg-emerald-500 text-white'
                                    : 'bg-red-500 text-white'
                              }`}>
                                {isDone ? '✓' : (isBuy ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />)}
                                {isDone ? 'DONE' : rec.action}
                              </span>
                              <span className={`text-sm font-semibold truncate ${isDone ? 'line-through text-muted-foreground' : ''}`}>{rec.sector}</span>
                            </div>
                            {!isDone && (
                              <span className={`shrink-0 text-sm font-mono font-bold ${isBuy ? 'text-emerald-700 dark:text-emerald-400' : 'text-red-600 dark:text-red-400'}`}>
                                {isBuy ? '+' : '-'}${rec.amount.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                              </span>
                            )}
                          </div>

                          {/* Per-ticker split pills */}
                          {!isDone && hasHoldings && (
                            <div className="mt-1.5 flex flex-wrap gap-1.5" data-testid={`rebalancer-rec-splits-${rec.sector}`}>
                              {rec.tickers.map((t, ti) => {
                                // For SELLs: compute whole shares to sell that sum <= split amount.
                                // If split >= ticker's total value -> mark as Sell All.
                                // For BUYs: show dollar amount, and if owned, suggest extra shares to buy.
                                let sharesLabel = null;
                                let amountLabel = `${isBuy ? '+' : '-'}$${splits[ti]}`;
                                let sellAll = false;

                                if (!isBuy) {
                                  const price = Number(t.price) || 0;
                                  const owned = Number(t.shares) || 0;
                                  const totalVal = Number(t.value) || owned * price;

                                  // Sector is being completely exited -> sell every share of this ticker.
                                  if (exitSector) {
                                    sellAll = true;
                                    amountLabel = `-$${Math.round(totalVal)}`;
                                    sharesLabel = `${owned % 1 === 0 ? owned : owned.toFixed(2)}sh`;
                                  } else {
                                    const target$ = splits[ti];
                                    if (target$ >= totalVal - 0.01 && totalVal > 0) {
                                      sellAll = true;
                                      amountLabel = `-$${Math.round(totalVal)}`;
                                      sharesLabel = `${owned % 1 === 0 ? owned : owned.toFixed(2)}sh`;
                                    } else if (price > 0) {
                                      const n = Math.floor(target$ / price);
                                      if (n >= 1) {
                                        sharesLabel = `${n}sh`;
                                        amountLabel = `-$${Math.round(n * price)}`;
                                      }
                                      // n === 0 (split amount < one share price) → just show the $ target
                                    }
                                  }
                                } else {
                                  // Buy pill: suggest how many shares that $ buys at current price
                                  const price = Number(t.price) || 0;
                                  if (price > 0) {
                                    const n = Math.floor(splits[ti] / price);
                                    if (n >= 1) sharesLabel = `${n}sh`;
                                  }
                                }

                                // "Sell All" pills are bigger & heavier so they jump out
                                const baseCls = sellAll
                                  ? 'inline-flex items-center gap-1.5 rounded-md px-2.5 py-1 text-xs font-mono font-bold cursor-pointer transition-all hover:scale-105 ring-2 ring-red-500/50 bg-red-200 text-red-900 dark:bg-red-900/60 dark:text-red-100 hover:bg-red-300 dark:hover:bg-red-800'
                                  : isBuy
                                    ? 'inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px] font-mono font-semibold cursor-pointer transition-all hover:scale-105 bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300 hover:bg-emerald-200 dark:hover:bg-emerald-900/60'
                                    : 'inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px] font-mono font-semibold cursor-pointer transition-all hover:scale-105 bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300 hover:bg-red-200 dark:hover:bg-red-900/60';

                                return (
                                  <span
                                    key={t.ticker}
                                    role="button"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      e.preventDefault();
                                      handleTickerClick(t.ticker, 'my-portfolio');
                                    }}
                                    className={baseCls}
                                    data-testid={`rebalancer-split-${rec.sector}-${t.ticker}`}
                                    title={`Click to manage ${t.ticker}`}
                                  >
                                    <span className="font-bold">{t.ticker}</span>
                                    {sharesLabel && <span className="opacity-80">· {sharesLabel}</span>}
                                    <span>{amountLabel}</span>
                                    {sellAll && (
                                      <span className="ml-0.5 px-1.5 py-0.5 rounded bg-red-600 text-white text-[10px] font-extrabold uppercase tracking-wide">Sell All</span>
                                    )}
                                  </span>
                                );
                              })}
                            </div>
                          )}

                          {isDone && (
                            <div className="mt-1 text-[10px] text-muted-foreground italic">
                              ✓ Target met — nothing more to do in this sector.
                            </div>
                          )}
                          {!isDone && isBuy && !hasHoldings && (
                            <div className="mt-1 text-[10px] text-emerald-700 dark:text-emerald-400 font-medium">
                              Click to browse {rec.sector} stocks →
                            </div>
                          )}
                          {!isDone && isBuy && hasHoldings && (
                            <div className="mt-1 text-[10px] text-emerald-700 dark:text-emerald-400 font-medium">
                              Click to browse more {rec.sector} stocks →
                            </div>
                          )}
                        </Wrapper>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </>
        )}
      </div>

      {/* Stock modal (opens when a ticker pill is clicked) */}
      <StockModal
        stock={selectedStock}
        open={modalOpen}
        onClose={() => {
          setModalOpen(false);
          setSelectedStock(null);
          setPrefetchedNews(null);
          setModalInitialTab(undefined);
        }}
        loading={loadingDetail}
        isInWatchlist={selectedStock ? watchlist.includes(selectedStock.ticker) : false}
        onToggleWatchlist={toggleWatchlist}
        isDark={isDark}
        prefetchedNews={prefetchedNews}
        user={user}
        initialTab={modalInitialTab}
        onPositionsChanged={() => {
          window.dispatchEvent(new Event('portfolio-changed'));
        }}
        onSelectStock={(t) => handleTickerClick(t)}
        onCompareClick={(t) => {
          if (t) { setCompareTicker(t); setCompareOpen(true); }
        }}
        watchlist={watchlist}
      />

      <CompareModal
        open={compareOpen}
        onClose={() => setCompareOpen(false)}
        initialTickerA={compareTicker}
        watchlist={watchlist}
        onTickerClick={() => {}}
      />

      {/* Cash deposit/withdraw confirm modal */}
      <Dialog open={!!cashConfirm} onOpenChange={(o) => { if (!o && !cashBusy) setCashConfirm(null); }}>
        <DialogContent className="sm:max-w-sm" data-testid="rebalancer-cash-confirm-modal">
          <DialogHeader>
            <DialogTitle className={cashConfirm?.kind === 'add' ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-600 dark:text-red-400'}>
              {cashConfirm?.kind === 'add' ? 'Add this much cash?' : 'Withdraw this much cash?'}
            </DialogTitle>
            <DialogDescription>
              <span className="block text-2xl font-bold font-mono text-foreground mt-2">
                ${(cashConfirm?.amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </span>
              <span className="block text-xs text-muted-foreground mt-2">
                {cashConfirm?.kind === 'add'
                  ? 'This will be added to your cash balance and available for buying stocks in this rebalance.'
                  : 'This will be removed from your cash balance. If the amount is greater than your balance, the balance will simply go to $0 — no error.'}
              </span>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setCashConfirm(null)} disabled={cashBusy} data-testid="rebalancer-cash-confirm-no">No</Button>
            <Button
              onClick={submitCashChange}
              disabled={cashBusy}
              className={cashConfirm?.kind === 'add'
                ? 'bg-emerald-600 hover:bg-emerald-700 text-white'
                : 'bg-red-600 hover:bg-red-700 text-white'}
              data-testid="rebalancer-cash-confirm-yes"
            >
              {cashBusy ? 'Saving...' : (cashConfirm?.kind === 'add' ? 'Yes, add cash' : 'Yes, withdraw')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Snap-to-100 confirmation modal */}
      <Dialog open={!!snapModal} onOpenChange={(o) => !o && setSnapModal(null)}>
        <DialogContent className="sm:max-w-sm" data-testid="rebalancer-snap-modal">
          <DialogHeader>
            <DialogTitle>
              You're at {snapModal?.sum}% total
            </DialogTitle>
            <DialogDescription>
              {snapModal?.direction === 'low'
                ? `That's ${100 - (snapModal?.sum || 0)}% low. Want me to bump "${snapModal?.sector}" up to hit exactly 100%?`
                : `That's ${(snapModal?.sum || 0) - 100}% over. Want me to pull "${snapModal?.sector}" down to hit exactly 100%?`}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setSnapModal(null)} data-testid="rebalancer-snap-no">No thanks</Button>
            <Button onClick={applySnap} data-testid="rebalancer-snap-yes">Yes, snap to 100%</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Over-buy warning: user bought more than their baseline cash + executed sells could cover */}
      <Dialog open={warningModal} onOpenChange={(o) => { if (!o) { setWarningModal(false); warningDismissedRef.current = true; } }}>
        <DialogContent className="sm:max-w-md" data-testid="rebalancer-warning-modal">
          <DialogHeader>
            <DialogTitle className="text-red-600 dark:text-red-400">
              Sell Your Stocks First
            </DialogTitle>
            <DialogDescription className="space-y-2">
              <span className="block">
                You've bought new positions, but haven't yet sold the stocks your plan called for.
                Without selling those first, your cash balance could go negative.
              </span>
              <span className="block">
                Please finish the SELL actions in your plan — or reset the plan if you've changed your mind.
              </span>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => { setWarningModal(false); warningDismissedRef.current = true; }}
              data-testid="rebalancer-warning-ok"
            >
              OK, I'll sell now
            </Button>
            <Button
              variant="destructive"
              onClick={() => { clearPlan(); resetTargets(); }}
              data-testid="rebalancer-warning-reset"
            >
              Reset Plan
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Round 95q — snapshot restore confirm */}
      <Dialog open={!!restoreTarget} onOpenChange={(o) => { if (!o && !restoring) setRestoreTarget(null); }}>
        <DialogContent className="sm:max-w-md" data-testid="rebalancer-restore-modal">
          <DialogHeader>
            <DialogTitle className="text-violet-700 dark:text-violet-300">
              Restore portfolio to this snapshot?
            </DialogTitle>
            <DialogDescription className="space-y-2">
              <span className="block text-sm font-mono font-bold text-foreground mt-1">
                {restoreTarget ? formatSnapDate(restoreTarget.created_at) : ''}
              </span>
              <span className="block text-xs">
                {restoreTarget?.position_count} positions (${Number(restoreTarget?.total_shares_value || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })} at snapshot prices)
                {' '}+ ${Number(restoreTarget?.cash_balance || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })} cash
              </span>
              <span className="block text-xs text-muted-foreground">
                Your current positions in this portfolio will be <strong>replaced entirely</strong> and your
                cash balance reset to the snapshot value. Any buys/sells you made while testing are undone.
                (Logged transaction history is not altered.) This can&apos;t be un-restored.
              </span>
              {/* Round 95t — show the user EXACTLY what they're about to
                  undo before they confirm. The compact diff line reads
                  like the message they get after a restore but in
                  reverse: "since you started testing: +12 EGGY, -10
                  TRIN, cash -$182" — so they know precisely what
                  reverts. */}
              {restoreTarget && (
                <RebalancerSnapshotDiff
                  snapshotId={restoreTarget.snapshot_id}
                  mode="compact"
                />
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setRestoreTarget(null)} disabled={restoring} data-testid="rebalancer-restore-no">Cancel</Button>
            <Button
              onClick={submitRestore}
              disabled={restoring}
              className="bg-violet-600 hover:bg-violet-700 text-white"
              data-testid="rebalancer-restore-yes"
            >
              {restoring ? 'Restoring…' : 'Yes, restore it'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Round 95t — standalone Compare-to-snapshot diff modal.
          Read-only view of what the testing session changed since
          the snapshot was saved. Provides a "Restore from here"
          shortcut that hands off to the existing Restore dialog. */}
      <Dialog open={!!compareTarget} onOpenChange={(o) => { if (!o) setCompareTarget(null); }}>
        <DialogContent className="sm:max-w-2xl" data-testid="rebalancer-compare-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-sky-700 dark:text-sky-300">
              <GitCompare className="w-4 h-4" />
              What changed since the snapshot?
            </DialogTitle>
            <DialogDescription className="text-xs">
              Read-only diff between the saved snapshot and your live portfolio. Nothing changes here — close this and your edits stay in place.
            </DialogDescription>
          </DialogHeader>
          <div className="max-h-[60vh] overflow-y-auto pr-1">
            {compareTarget && (
              <RebalancerSnapshotDiff
                snapshotId={compareTarget.snapshot_id}
                mode="full"
              />
            )}
          </div>
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => setCompareTarget(null)}
              data-testid="rebalancer-compare-close"
            >
              Close
            </Button>
            <Button
              onClick={() => {
                const snap = compareTarget;
                setCompareTarget(null);
                setRestoreTarget(snap);
              }}
              className="bg-violet-600 hover:bg-violet-700 text-white"
              data-testid="rebalancer-compare-restore"
            >
              <RefreshCw className="w-3.5 h-3.5 mr-1.5" />
              Restore from here
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <SiteDisclaimer />
    </div>
  );
}
